import { collection, getDocs, query, orderBy, limit, onSnapshot } from 'firebase/firestore'
import ImageUploader from '@components/ImageUploader'
import styles from '@styles/CrudProduct.module.css'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { db } from '@lib/firebase'

const OfferForm = ({ offer, onSubmit, darkMode, isEditing = false }) => {
  const [formData, setFormData] = useState({
    id: '',
    image: '',
    title: '',
    description: '',
    originalPrice: 0,
    discountedPrice: 0,
    discountPercentage: 0,
    active: true,
    validUntil: '',
  })

  useEffect(() => {
    offer ? setFormData(offer) : getNextAvailableId()

    // Escuchar los cambios en la colección de ofertas
    const unsubscribe = onSnapshot(collection(db, 'offers'), () => {
      if (!isEditing) {
        getNextAvailableId()
      }
    })

    return () => unsubscribe()
  }, [offer, isEditing])

  const getNextAvailableId = async () => {
    const offersRef = collection(db, 'offers')
    const q = query(offersRef, orderBy('id', 'desc'), limit(1))
    const querySnapshot = await getDocs(q)

    let nextId = 1
    if (!querySnapshot.empty) {
      const lastOffer = querySnapshot.docs[0].data()
      nextId = Number.parseInt(lastOffer.id) + 1
    }

    // Verificar si hay espacios en los IDs
    const allOffers = await getDocs(offersRef)
    const usedIds = allOffers.docs.map(doc => Number.parseInt(doc.data().id)).sort((a, b) => a - b)
    for (let i = 1; i < nextId; i++) {
      if (!usedIds.includes(i)) {
        nextId = i
        break
      }
    }

    setFormData(p => ({ ...p, id: nextId.toString() }))
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(p => ({ ...p, [name]: value }))
  }

  const handleImageUpload = imageUrl => {
    setFormData(p => ({ ...p, image: imageUrl }))
  }

  const calculateDiscountPercentage = (original, discounted) => {
    const percentage = ((original - discounted) / original) * 100
    return Math.round(percentage)
  }

  const handlePriceChange = e => {
    const { name, value } = e.target
    const numValue = Number.parseFloat(value)

    if (name === 'originalPrice') {
      const discountPercentage = calculateDiscountPercentage(
        numValue,
        formData.discountedPrice
      )
      setFormData(p => ({
        ...p,
        [name]: numValue,
        discountPercentage,
      }))
    } else if (name === 'discountedPrice') {
      const discountPercentage = calculateDiscountPercentage(
        formData.originalPrice,
        numValue
      )
      setFormData(p => ({
        ...p,
        [name]: numValue,
        discountPercentage,
      }))
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    onSubmit(formData)
    if (!isEditing) {
      setFormData({
        id: '',
        image: '',
        title: '',
        description: '',
        originalPrice: 0,
        discountedPrice: 0,
        discountPercentage: 0,
        active: true,
        validUntil: '',
      })
      setTimeout(() => {
        getNextAvailableId()
      }, 1000)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`${styles.form} ${darkMode ? styles.darkForm : ''}`}
    >
      <h2 className={styles.subtitle}>
        {isEditing ? 'Editar oferta' : 'Agregar nueva oferta'}
      </h2>
      <div className={styles.grid}>
        <ImageUploader
          onImageUpload={handleImageUpload}
          currentImage={formData.image}
        />

        <div>
          <label htmlFor='image'>Directorio de Imagen</label>
            <input
              id='image'
              name='image'
              type='text'
              value={formData.image.url || formData.image}
              onChange={handleChange}
              className={darkMode ? styles.darkInput : ''}
              required
              disabled={!isEditing}
            />
        </div>

        <div>
          <label htmlFor='id'>ID</label>
          <input
            id='id'
            name='id'
            type='text'
            value={formData.id}
            onChange={handleChange}
            className={darkMode ? styles.darkInput : ''}
            required
            disabled={!isEditing}
          />
        </div>

        <div>
          <label htmlFor='title'>Título</label>
          <input
            id='title'
            name='title'
            type='text'
            value={formData.title}
            onChange={handleChange}
            className={darkMode ? styles.darkInput : ''}
            required
          />
        </div>

        <div>
          <label htmlFor='description'>Descripción</label>
          <textarea
            id='description'
            name='description'
            value={formData.description}
            onChange={handleChange}
            className={darkMode ? styles.darkInput : ''}
            required
          />
        </div>

        <div>
          <label htmlFor='originalPrice'>Precio Original</label>
          <input
            id='originalPrice'
            name='originalPrice'
            type='number'
            value={formData.originalPrice}
            onChange={handlePriceChange}
            className={darkMode ? styles.darkInput : ''}
            required
          />
        </div>

        <div>
          <label htmlFor='discountedPrice'>Precio con Descuento</label>
          <input
            id='discountedPrice'
            name='discountedPrice'
            type='number'
            value={formData.discountedPrice}
            onChange={handlePriceChange}
            className={darkMode ? styles.darkInput : ''}
            required
          />
        </div>

        <div>
          <label htmlFor='validUntil'>Válido Hasta</label>
          <input
            id='validUntil'
            name='validUntil'
            type='date'
            value={formData.validUntil}
            onChange={handleChange}
            className={darkMode ? styles.darkInput : ''}
            required
          />
        </div>
      </div>
      <button type='submit' className={styles.addButton}>
        {isEditing ? 'Actualizar Oferta' : 'Agregar Oferta'}
      </button>
    </form>
  )
}

OfferForm.propTypes = {
  offer: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  darkMode: PropTypes.bool,
  isEditing: PropTypes.bool,
}

export default OfferForm
